import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { Ability } from 'ember-can';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import { EMPLOYEE, GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST } from 'garaje/utils/roles';
import _intersection from 'lodash/intersection';

const CAN_EXPORT_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST];

const CAN_SEE_DELETED_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST];

const CAN_VISIT_ROLES = [EMPLOYEE, GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST];

const CAN_SEE_DIRECTORY_DETAILS_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN];

const CAN_MANUALLY_ADD = [GLOBAL_ADMIN, LOCATION_ADMIN];

export default class EmployeeAbility extends Ability {
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare featureConfig: FeatureConfigService;

  get canVisit(): boolean {
    const currentAdmin = this.currentAdmin;
    const roleNames = currentAdmin.roleNames;
    const employeeDirectoryEnabled = !this.featureFlags.isEnabled('disable-employee-directory');
    const allowed = isPresent(_intersection(CAN_VISIT_ROLES, roleNames));

    if (allowed && roleNames.length === 1 && roleNames.includes(EMPLOYEE)) {
      // This scenario covers users with a location-user of type Employee
      return employeeDirectoryEnabled;
    } else if (!allowed) {
      /*
       There can be location users with a role different to Employee
       who still have a matching employee in the db. If
       employeeDirectoryEnabled then they should be allowd to access it.
       */
      return employeeDirectoryEnabled && Boolean(currentAdmin.employee);
    } else {
      return allowed;
    }
  }

  get canSeeDeleted(): boolean {
    const { roleNames } = this.currentAdmin;
    return isPresent(_intersection(CAN_SEE_DELETED_ROLES, roleNames));
  }

  get canSeeDirectoryDetails(): boolean {
    const { roleNames } = this.currentAdmin;
    return (
      isPresent(_intersection(CAN_SEE_DIRECTORY_DETAILS_ROLES, roleNames)) ||
      this.featureConfig.isEnabled('employeePrivateData') // employeePrivateData is to be interpreted as "Should we show employee private data?"
    );
  }

  get canExport(): boolean {
    const { roleNames } = this.currentAdmin;
    return isPresent(_intersection(CAN_EXPORT_ROLES, roleNames));
  }

  get canManuallyAdd(): boolean {
    const { roleNames } = this.currentAdmin;
    return isPresent(_intersection(CAN_MANUALLY_ADD, roleNames));
  }
}
